import { Button, useMediaQuery } from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Outlet, useParams } from 'react-router';
import { LeftPanelSectionTemplate } from '../../../components/AdminTemplates';
import OutletContextReliantRoute from '../../../components/OutletContextReliantRoute';
import { SideActionItem } from '../../../components/SideActionsList';
import { useIotsApi } from '../../../contexts/iotsApi';
import { useLanguage } from '../../../contexts/language';
import useQueryErrorNotifier from '../../../hooks/useQueryErrorNotifier';
import { StatusIcon } from '../../../utils/icon';
import { UserTemplateContext } from './types';
import UserStateToggleDialog from './UserStateToggleDialog';
import UserStatusBadge from './UserStatusBadge';
import { User } from '../../../types/user';

const UserTemplate = () => {
  const { t } = useLanguage();
  const { userId: userIdQueryParam } = useParams();
  const { getUserById } = useIotsApi();
  const [displayedUserName, setDisplayedUserName] = useState<string>('');
  const isMdUp = useMediaQuery('(min-width:768px)');

  const [toggleStateUser, setToggleStateUser] = useState<User | null>(null);

  const userId = useMemo(() => {
    return typeof userIdQueryParam !== 'string' ||
      userIdQueryParam.toLocaleLowerCase() === 'create'
      ? null
      : userIdQueryParam;
  }, [userIdQueryParam]);

  const {
    data: user = null,
    isPending,
    isFetching,
    refetch,
    error,
  } = useQuery({
    queryKey: ['user', userId ?? ''],
    queryFn: () => (userId ? getUserById(userId) : Promise.resolve(null)),
    refetchOnMount: 'always',
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  useQueryErrorNotifier([error]);

  const queryClient = useQueryClient();

  const contextValue = useMemo<UserTemplateContext>(
    () => ({
      userId: userId,
      user: user,
      refetch: () => void refetch(),
      updateContextUser: updatedUser =>
        queryClient.setQueryData<User>(['user', userId ?? ''], updatedUser),
      setDisplayedUserName: setDisplayedUserName,
      isPending,
      isFetching,
    }),
    [user, isPending, isFetching, refetch, queryClient, userId],
  );

  const sidePanelActions = useMemo<SideActionItem[]>(() => {
    return [
      {
        id: 'information',
        label: t('content.information'),
        statusIcon: user ? StatusIcon.Completed : StatusIcon.Pending,
        url: `/admin/users/${userId ?? 'create'}`,
        isExactUrl: true,
      },
    ];
  }, [t, user, userId]);

  const isUsingTheNetwork = isPending || isFetching;

  return (
    <LeftPanelSectionTemplate
      title={
        user ? (
          <span className="inline-flex flex-wrap items-center gap-2 md:items-end md:gap-6">
            {`${user.firstName} ${user.lastName}`}
            <UserStatusBadge isActive={user.isActive} small={!isMdUp} />
          </span>
        ) : (
          displayedUserName.trim() || t('content.untitled')
        )
      }
      isLoading={isUsingTheNetwork}
      leftPanelActions={sidePanelActions}
      goBackLinkText={t('content.back-to-users')}
      goBackLinkUrl="/admin/users"
      actions={
        user && (
          <>
            <Button
              className="mr-auto"
              color={user.isActive ? 'error' : undefined}
              variant="outlined"
              type="button"
              onClick={() => {
                setToggleStateUser(user);
              }}
              disabled={isUsingTheNetwork}
            >
              {t(`content.${user.isActive ? 'deactivate' : 'activate'}`)}
            </Button>
            <UserStateToggleDialog
              user={toggleStateUser}
              onClose={() => setToggleStateUser(null)}
              onConfirm={updatedUser => {
                contextValue.updateContextUser({
                  ...updatedUser,
                  ...user,
                });
                contextValue.refetch();
              }}
            />
          </>
        )
      }
    >
      <Outlet context={contextValue} />
    </LeftPanelSectionTemplate>
  );
};

export default UserTemplate;

export const UserSubPageWrapper = () => (
  <OutletContextReliantRoute<UserTemplateContext>
    getOutletContextState={({ user, userId, isPending }) =>
      // If the user has been deleted, it should not be accessible
      user ? 'ready' : userId && isPending ? 'pending' : 'error'
    }
    errorRedirectionPath="/admin/users"
  />
);
